import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('request'); // 'request', 'reset', 'success', 'error'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Проверяем, есть ли параметры сброса в URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');

    if (mode === 'resetPassword' && oobCode) {
      // Режим сброса пароля (пользователь перешел по ссылке из письма)
      setStatus('reset');
    }
  }, [location]);

  // Отправка email для сброса пароля
  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Введите email');
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/reset-password`,
        handleCodeInApp: false
      });
      
      setStatus('success');
      setMessage('✅ Ссылка для сброса пароля отправлена на ваш email. Проверьте почту!');
    } catch (error) {
      console.error('Reset password error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setMessage('❌ Пользователь с таким email не найден');
      } else if (error.code === 'auth/invalid-email') {
        setMessage('❌ Неверный формат email');
      } else if (error.code === 'auth/too-many-requests') {
        setMessage('❌ Слишком много запросов. Попробуйте позже');
      } else {
        setMessage('❌ Ошибка при отправке письма. Попробуйте позже');
      }
    } finally {
      setLoading(false);
    }
  };

  // Установка нового пароля
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setMessage('Заполните все поля');
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage('Пароль должен содержать минимум 6 символов');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setMessage('Пароли не совпадают');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const auth = getAuth();
      const urlParams = new URLSearchParams(location.search);
      const oobCode = urlParams.get('oobCode');
      
      if (!oobCode) {
        setMessage('❌ Неверная ссылка для сброса пароля');
        return;
      }

      // Проверяем валидность кода
      await verifyPasswordResetCode(auth, oobCode);
      
      // Подтверждаем сброс пароля
      await confirmPasswordReset(auth, oobCode, newPassword);
      
      setStatus('success');
      setMessage('✅ Пароль успешно изменен! Теперь вы можете войти в аккаунт.');
      
      // Перенаправляем на страницу входа через 3 секунды
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      
      if (error.code === 'auth/expired-action-code') {
        setMessage('❌ Срок действия ссылки истек. Запросите новое письмо');
      } else if (error.code === 'auth/invalid-action-code') {
        setMessage('❌ Неверная ссылка сброса пароля');
      } else if (error.code === 'auth/weak-password') {
        setMessage('❌ Пароль слишком слабый');
      } else {
        setMessage('❌ Ошибка при сбросе пароля. Попробуйте позже');
      }
    } finally {
      setLoading(false);
    }
  };

  // Форма запроса сброса пароля
  if (status === 'request') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#180749] to-[#2a0f6e] flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Восстановление пароля</h2>
            <p className="text-white/70 text-sm">
              Введите ваш email, и мы отправим ссылку для сброса пароля
            </p>
          </div>

          <form onSubmit={handleSendResetEmail} className="space-y-4">
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-purple-500 transition"
                placeholder="your@email.com"
                required
              />
            </div>

            {message && (
              <div className={`p-3 rounded-xl text-sm ${
                message.includes('✅') 
                  ? 'bg-green-500/20 text-green-300' 
                  : 'bg-red-500/20 text-red-300'
              }`}>
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Отправка...' : 'Отправить ссылку'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full py-2 rounded-xl bg-white/10 text-white/80 font-semibold hover:bg-white/20 transition"
            >
              Вернуться к входу
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Форма установки нового пароля
  if (status === 'reset') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#180749] to-[#2a0f6e] flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Создание нового пароля</h2>
            <p className="text-white/70 text-sm">
              Придумайте новый пароль для вашего аккаунта
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Новый пароль
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-purple-500 transition"
                placeholder="минимум 6 символов"
                required
              />
            </div>

            <div>
              <label className="block text-white/80 text-sm font-medium mb-2">
                Подтвердите пароль
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-purple-500 transition"
                placeholder="повторите пароль"
                required
              />
            </div>

            {message && (
              <div className={`p-3 rounded-xl text-sm ${
                message.includes('✅') 
                  ? 'bg-green-500/20 text-green-300' 
                  : 'bg-red-500/20 text-red-300'
              }`}>
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить пароль'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Успешная страница
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#180749] to-[#2a0f6e] flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-md text-center">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {message.includes('пароль') ? 'Пароль изменен!' : 'Письмо отправлено!'}
        </h2>
        <p className="text-white/70 mb-4">{message}</p>
        {message.includes('ссылка') && (
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:opacity-90 transition"
          >
            На страницу входа
          </button>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;